import { useState, useEffect } from "react";
import { Loader2, CheckCircle2, Package, Wallet, CreditCard } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { inr } from "@/lib/format";
import { AccountBalanceBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";

export default function PaymentDialog({
  open,
  onOpenChange,
  patient,
  defaultAmount,
  onSaved,
  service = "clinic",
}) {
  const { user, settings } = useAuth();
  const [amount, setAmount] = useState("");
  const [payUse, setPayUse] = useState("normal"); // "normal" | "package"
  const [method, setMethod] = useState("cash");
  const [collectedBy, setCollectedBy] = useState("receptionist");
  const [notes, setNotes] = useState("");
  const [reference, setReference] = useState("");

  // Package fields
  const [pkgName, setPkgName] = useState("");
  const [pkgSessions, setPkgSessions] = useState("10");
  const [pkgExpiry, setPkgExpiry] = useState("");
  const [pkgReason, setPkgReason] = useState("");

  const [loading, setLoading] = useState(false);

  const curBalance = Number(patient?.account_balance ?? patient?.advance_balance ?? 0);

  useEffect(() => {
    if (open) {
      if (defaultAmount != null) {
        setAmount(String(defaultAmount));
      } else if (curBalance < 0) {
        setAmount(String(Math.abs(curBalance)));
      } else if (patient?.session_fee) {
        setAmount(String(patient.session_fee));
      } else {
        setAmount("");
      }
      setPayUse("normal");
      setMethod("cash");
      setCollectedBy(
        user?.role === "physiotherapist"
          ? "physiotherapist"
          : service === "home"
          ? "owner"
          : "receptionist"
      );
      setNotes("");
      setReference("");
      setPkgName("");
      setPkgSessions("10");
      setPkgExpiry("");
      setPkgReason("");
    }
  }, [open, patient, defaultAmount, curBalance, service, user]);

  const methods = settings?.payment_methods || ["cash", "upi", "bank", "card", "cheque", "other"];

  const submit = async () => {
    const numAmt = Number(amount);
    if (!amount || numAmt <= 0) {
      toast.error("Please enter a valid payment amount");
      return;
    }

    const isPkg = payUse === "package";
    if (isPkg && (!pkgSessions || Number(pkgSessions) <= 0)) {
      toast.error("Please specify valid total sessions for package");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        patient_id: patient.id,
        amount: numAmt,
        method,
        type: isPkg ? "package" : "account_credit",
        service,
        collected_by: collectedBy,
        notes,
        reference,
        is_package: isPkg,
        package_name: isPkg ? pkgName || `${pkgSessions}-Session Package` : "",
        package_sessions: isPkg ? Number(pkgSessions) : null,
        package_expiry_date: isPkg ? pkgExpiry || null : null,
        package_reason: isPkg ? pkgReason : "",
      };

      const res = await api.post("/payments", payload);
      const recMsg = res.data.receipt_no ? ` · Receipt: ${res.data.receipt_no}` : "";
      toast.success(`Payment recorded successfully of ₹${numAmt.toLocaleString("en-IN")}${recMsg}`);
      onOpenChange(false);
      onSaved?.(res.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  const perSessionRate =
    payUse === "package" && Number(amount) > 0 && Number(pkgSessions) > 0
      ? (Number(amount) / Number(pkgSessions)).toFixed(2)
      : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2 text-bk-blue">
            <Wallet className="h-5 w-5" /> Add Money to Account
          </DialogTitle>
        </DialogHeader>

        {/* Patient Status Overview */}
        <div className="bg-slate-50 rounded-xl p-3.5 border border-slate-200/80 flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="font-bold text-slate-800 text-sm truncate">{patient?.name}</p>
            <p className="text-xs text-slate-500 font-mono mt-0.5">
              {patient?.patient_id || patient?.patient_code || "BK-PATIENT"}
            </p>
          </div>
          <div className="text-right shrink-0">
            <span className="text-[11px] text-slate-400 block mb-0.5">Current Balance:</span>
            <AccountBalanceBadge balance={curBalance} />
          </div>
        </div>

        <div className="space-y-4 my-1">
          {/* Payment Usage Selection */}
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-600">Payment Usage</Label>
            <RadioGroup
              value={payUse}
              onValueChange={setPayUse}
              className="grid grid-cols-2 gap-2.5"
            >
              <div
                className={`flex items-center space-x-2 border p-3 rounded-lg cursor-pointer transition-all ${
                  payUse === "normal"
                    ? "border-emerald-500 bg-emerald-50/40 text-emerald-900 shadow-sm"
                    : "border-slate-200 hover:border-slate-300"
                }`}
                onClick={() => setPayUse("normal")}
              >
                <RadioGroupItem value="normal" id="pay-normal" />
                <Label htmlFor="pay-normal" className="cursor-pointer text-xs font-medium">
                  Normal Account Credit
                  <span className="block text-[10px] text-slate-500 font-normal">
                    Auto-settles due & adds credit
                  </span>
                </Label>
              </div>

              <div
                className={`flex items-center space-x-2 border p-3 rounded-lg cursor-pointer transition-all ${
                  payUse === "package"
                    ? "border-indigo-500 bg-indigo-50/40 text-indigo-900 shadow-sm"
                    : "border-slate-200 hover:border-slate-300"
                }`}
                onClick={() => setPayUse("package")}
              >
                <RadioGroupItem value="package" id="pay-package" />
                <Label htmlFor="pay-package" className="cursor-pointer text-xs font-medium">
                  Create Custom Package
                  <span className="block text-[10px] text-slate-500 font-normal">
                    Fixed sessions agreement
                  </span>
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Amount & Method */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Amount Received (₹) *</Label>
              <Input
                data-testid="payment-amount"
                type="number"
                placeholder="e.g. 1000"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="font-bold text-base tabular-nums"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Payment Method *</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger data-testid="payment-method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {methods.map((m) => (
                    <SelectItem key={m} value={m} className="uppercase text-xs font-medium">
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Custom Package Form Fields */}
          {payUse === "package" && (
            <div className="bg-indigo-50/60 border border-indigo-200/80 rounded-xl p-3.5 space-y-3">
              <div className="flex items-center gap-1.5 text-xs font-bold text-indigo-900">
                <Package className="h-4 w-4 text-indigo-600" /> Custom Package Terms
              </div>

              <div className="grid grid-cols-2 gap-2.5">
                <div className="space-y-1">
                  <Label className="text-[11px] font-semibold text-slate-700">Package Name</Label>
                  <Input
                    placeholder="e.g. 10 Sessions Spine Care"
                    value={pkgName}
                    onChange={(e) => setPkgName(e.target.value)}
                    className="h-8 text-xs bg-white"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px] font-semibold text-slate-700">Total Sessions *</Label>
                  <Input
                    type="number"
                    placeholder="10"
                    value={pkgSessions}
                    onChange={(e) => setPkgSessions(e.target.value)}
                    className="h-8 text-xs bg-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2.5">
                <div className="space-y-1">
                  <Label className="text-[11px] font-semibold text-slate-700">Expiry Date (Optional)</Label>
                  <Input
                    type="date"
                    value={pkgExpiry}
                    onChange={(e) => setPkgExpiry(e.target.value)}
                    className="h-8 text-xs bg-white"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px] font-semibold text-slate-700">Approval / Reason</Label>
                  <Input
                    placeholder="e.g. Special Discount agreed"
                    value={pkgReason}
                    onChange={(e) => setPkgReason(e.target.value)}
                    className="h-8 text-xs bg-white"
                  />
                </div>
              </div>

              {perSessionRate && (
                <p className="text-xs text-indigo-700 font-medium pt-1">
                  💡 Rate: <strong>₹{perSessionRate} / session</strong> ({pkgSessions} sessions total)
                </p>
              )}
            </div>
          )}

          {/* Reference & Collected By */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Transaction Ref / UPI ID</Label>
              <Input
                placeholder="Optional"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                className="h-9 text-xs"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Collected By</Label>
              <Select value={collectedBy} onValueChange={setCollectedBy}>
                <SelectTrigger data-testid="payment-collected-by" className="h-9 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="receptionist">Receptionist</SelectItem>
                  <SelectItem value="owner">Owner / Clinic Admin</SelectItem>
                  <SelectItem value="physiotherapist">Physiotherapist</SelectItem>
                  <SelectItem value="online">Online / Direct</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Notes (Optional)</Label>
            <Textarea
              rows={2}
              placeholder="e.g. Paid in full for upcoming visits"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="text-xs"
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={loading}
            data-testid="payment-save"
            className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5 font-semibold"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Record & Settle"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

